import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Sparkles, Loader2, CheckCircle2, Eye, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function BulkAnalyzerDialog({ onComplete }) {
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(25);
  const [state, setState] = useState("");
  const [minScore, setMinScore] = useState("");
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const start = async () => {
    try {
      const filters = {};
      if (state) filters.state = state;
      if (minScore) filters.min_score = parseFloat(minScore);
      const { data } = await api.post("/ai/investment-analysis/bulk", { limit: parseInt(limit), filters });
      setJob(data);
      poll(data.id);
    } catch (e) { toast.error("Failed to start job"); }
  };

  const poll = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/ai/investment-analysis/jobs/${jobId}`);
        setJob(r.data);
        if (r.data.status === "complete") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.success(`Bulk analysis complete: ${r.data.processed} analyzed`);
          onComplete?.();
        }
      } catch { /* ignore */ }
    }, 2500);
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const progressPct = job?.total ? Math.round((job.processed / job.total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v && !job) { setJob(null); } }}>
      <DialogTrigger asChild>
        <button data-testid="bulk-analyzer-btn" className="flex items-center gap-2 px-4 py-2 rounded border border-[#4a3a2b] text-[13px] text-[#d4a574] hover:border-[#d4a574]">
          <Sparkles size={14} /> Bulk Analyze
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#1c1712] border-[#33291f] text-[#f4ede2] max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Bulk AI Investment Underwriter</DialogTitle></DialogHeader>
        {!job ? (
          <div className="space-y-3" data-testid="bulk-form">
            <p className="text-[13px] text-[#b8ac96]">Run deep AI investment analysis across a filtered set of unanalyzed opportunities. Runs in the background — you can close this dialog and check the Investment Insights page.</p>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">How many opportunities</label>
              <Input type="number" min={1} max={200} value={limit} onChange={(e) => setLimit(e.target.value)} data-testid="bulk-limit" className="bg-[#241d16] border-[#33291f] mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">State (optional)</label>
                <Input value={state} onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))} placeholder="CA" data-testid="bulk-state" className="bg-[#241d16] border-[#33291f] mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">Min AI Score</label>
                <Input type="number" value={minScore} onChange={(e) => setMinScore(e.target.value)} placeholder="70" data-testid="bulk-min-score" className="bg-[#241d16] border-[#33291f] mt-1" />
              </div>
            </div>
            <button onClick={start} data-testid="bulk-start" className="w-full py-2 rounded bg-[#d4a574] text-[#14100c] font-semibold flex items-center justify-center gap-2">
              <Sparkles size={14} /> Start Bulk Analysis
            </button>
            <div className="text-[10px] text-[#7a6d5a] font-mono">Estimated: ~{Math.ceil(limit * 8 / 60)} minutes for {limit} opportunities.</div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="bulk-progress">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#d4a574] font-semibold">Job {job.id.slice(0,8)}</div>
              <span className={`text-[10px] font-mono uppercase tracking-wider ${job.status === "complete" ? "text-[#34d399]" : job.status === "running" ? "text-[#fbbf24]" : "text-[#7a6d5a]"}`}>{job.status}</span>
            </div>
            <div>
              <div className="flex items-baseline justify-between mb-1">
                <span className="font-mono text-3xl font-bold">{job.processed}<span className="text-[13px] text-[#7a6d5a]"> / {job.total}</span></span>
                <span className="text-[10px] font-mono text-[#d4a574]">{progressPct}%</span>
              </div>
              <div className="h-1.5 rounded bg-[#33291f] overflow-hidden">
                <div className="h-full bg-[#d4a574] [transition:width_400ms_ease]" style={{ width: `${progressPct}%` }} />
              </div>
            </div>
            {job.current_title && <div className="text-[11px] text-[#b8ac96] flex items-center gap-2"><Loader2 size={11} className="animate-spin" /> {job.current_title}</div>}
            <div className="grid grid-cols-3 gap-2">
              <div className="border border-[#33291f] rounded p-2 text-center">
                <CheckCircle2 size={12} className="mx-auto text-[#34d399] mb-0.5" />
                <div className="font-mono text-lg font-bold text-[#34d399]">{job.rec_counts?.buy || 0}</div>
                <div className="text-[9px] uppercase tracking-wider text-[#7a6d5a]">Buy</div>
              </div>
              <div className="border border-[#33291f] rounded p-2 text-center">
                <Eye size={12} className="mx-auto text-[#fbbf24] mb-0.5" />
                <div className="font-mono text-lg font-bold text-[#fbbf24]">{job.rec_counts?.watch || 0}</div>
                <div className="text-[9px] uppercase tracking-wider text-[#7a6d5a]">Watch</div>
              </div>
              <div className="border border-[#33291f] rounded p-2 text-center">
                <XCircle size={12} className="mx-auto text-[#f87171] mb-0.5" />
                <div className="font-mono text-lg font-bold text-[#f87171]">{job.rec_counts?.skip || 0}</div>
                <div className="text-[9px] uppercase tracking-wider text-[#7a6d5a]">Skip</div>
              </div>
            </div>
            {job.errors > 0 && <div className="text-[11px] text-[#f87171]">{job.errors} errors — see audit logs.</div>}
            {job.status === "complete" && (
              <button onClick={() => { setJob(null); setOpen(false); }} data-testid="bulk-close" className="w-full py-2 rounded bg-[#d4a574] text-[#14100c] font-semibold">Done</button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
